/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import {
  CommunityType,
  getCommunityMemberCount,
  useRequestCommunitiesAdminCommunity,
  useRequestCommunitiesChildren,
} from "@packages/sdk";
import type { CSSColorStrict, ThemeType } from "@packages/ui";
import {
  AvatarCommunity,
  CheckmarkIcon,
  ListProfile,
  spacing,
  useIntersectionObserverRefState,
} from "@packages/ui";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef } from "react";

const checkmarkStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedium,
});

const communitiesListStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  padding: spacing.s,
};

const communityStyles: Interpolation = {
  cursor: "pointer",
};

const communitySelectedStyles: Interpolation<ThemeType> = (
  theme: ThemeType,
) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
});

const stylesLoading = {
  flex: {
    display: "flex",
  },
  hidden: {
    display: "none",
  },
};

type BroadcastCreateSentToListProps = {
  communityId: number;
  communityIds: number[];
  setCommunityIds: Dispatch<SetStateAction<number[]>>;
};

export const BroadcastCreateSentToList = ({
  communityId,
  communityIds,
  setCommunityIds,
}: BroadcastCreateSentToListProps) => {
  const t = useTranslations();

  const { query: queryCommunitiesAdminCommunity } =
    useRequestCommunitiesAdminCommunity({ id: communityId });
  const isNetwork =
    queryCommunitiesAdminCommunity.data?.type === CommunityType.network;

  const infiniteScrollBufferRef = useRef<HTMLDivElement>(null);
  const [infiniteScrollBufferRefShowing] = useIntersectionObserverRefState({
    ref: infiniteScrollBufferRef,
  });

  const { infiniteQuery: communitiesChildrenBasicOrSmallGroupInfiniteQuery } =
    useRequestCommunitiesChildren({
      id: communityId,
      type: isNetwork ? CommunityType.basic : CommunityType.small_group,
    });
  const { infiniteQuery: communitiesChildrenParishInfiniteQuery } =
    useRequestCommunitiesChildren({
      enabled: isNetwork,
      id: communityId,
      type: CommunityType.parish,
    });

  const handleCommunityOnClick = ({ id }: { id: number }) => {
    if (communityIds.includes(id))
      return setCommunityIds((prev) => prev.filter((prevId) => prevId !== id));
    return setCommunityIds([...communityIds, id]);
  };

  const shouldFetchNextPagecommunitiesChildrenBasicOrSmallGroupInfiniteQuery =
    infiniteScrollBufferRefShowing &&
    communitiesChildrenBasicOrSmallGroupInfiniteQuery.hasNextPage &&
    !communitiesChildrenBasicOrSmallGroupInfiniteQuery.isFetchingNextPage;

  const shouldFetchNextPageCommunitiesChildrenParishInfiniteQuery =
    infiniteScrollBufferRefShowing &&
    communitiesChildrenParishInfiniteQuery.hasNextPage &&
    !communitiesChildrenParishInfiniteQuery.isFetchingNextPage;

  useEffect(() => {
    if (shouldFetchNextPagecommunitiesChildrenBasicOrSmallGroupInfiniteQuery)
      communitiesChildrenBasicOrSmallGroupInfiniteQuery.fetchNextPage();
  }, [shouldFetchNextPagecommunitiesChildrenBasicOrSmallGroupInfiniteQuery]);

  useEffect(() => {
    if (shouldFetchNextPageCommunitiesChildrenParishInfiniteQuery)
      communitiesChildrenParishInfiniteQuery.fetchNextPage();
  }, [shouldFetchNextPageCommunitiesChildrenParishInfiniteQuery]);

  const childCommunities = [
    ...(isNetwork ? [] : [queryCommunitiesAdminCommunity.data]),
    ...[
      ...(communitiesChildrenBasicOrSmallGroupInfiniteQuery.data?.pages ?? []),
      ...(communitiesChildrenParishInfiniteQuery.data?.pages ?? []),
    ].flatMap((page) => page.results),
  ];

  return (
    <div css={communitiesListStyles}>
      {childCommunities.map((community) => (
        <ListProfile
          avatar={
            <AvatarCommunity
              color={community.color_hex as CSSColorStrict}
              imgSrc={community.image_url}
              key="avatar"
              name={community.name}
              size="m"
            />
          }
          description={community.parent_details?.name}
          icon={
            communityIds.includes(community.id) ? (
              <CheckmarkIcon.SimpleOn css={checkmarkStyles} />
            ) : null
          }
          key={community.id}
          metadata={`${community.simple_address_label}${community.simple_address_label && getCommunityMemberCount({ community }) >= 0 ? " | " : ""}${t(
            "create_community_content_member_count",
            {
              create_community_content_member_count: getCommunityMemberCount({
                community,
              }),
            },
          )}`}
          onClick={() => handleCommunityOnClick({ id: community.id })}
          css={[
            communityStyles,
            communityIds.includes(community.id)
              ? communitySelectedStyles
              : null,
          ]}
          title={community.name}
        />
      ))}
      <ListProfile
        loading
        ref={infiniteScrollBufferRef}
        css={[
          communitiesChildrenBasicOrSmallGroupInfiniteQuery.hasNextPage ||
          communitiesChildrenParishInfiniteQuery.hasNextPage
            ? stylesLoading.flex
            : stylesLoading.hidden,
        ]}
        title=""
      />
    </div>
  );
};
